import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Undo as UndoIcon,
  Info as InfoIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

interface AdvancedSchemaEditorProps {
  schema: any;
  manualEdits?: Record<string, { edited_at: string; edited_by: string }>;
  verificationStatus?: Record<string, any>;
  onChange: (fieldPath: string, value: any) => void;
  onResetField?: (fieldPath: string) => void;
}

interface FieldGroup {
  title: string;
  icon?: React.ReactNode;
  fields: FieldDefinition[];
}

interface FieldDefinition {
  path: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'array' | 'object' | 'boolean' | 'json';
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  options?: { value: string; label: string }[];
  description?: string;
  placeholder?: string;
  validation?: (value: any) => string | null;
}

const schemaTypeOptions = [
  { value: 'LocalBusiness', label: 'Local Business' },
  { value: 'Restaurant', label: 'Restaurant' },
  { value: 'TouristAttraction', label: 'Tourist Attraction' },
  { value: 'Hotel', label: 'Hotel' },
  { value: 'Event', label: 'Event' },
  { value: 'SportsEvent', label: 'Sports Event' },
  { value: 'Product', label: 'Product' },
  { value: 'Service', label: 'Service' },
  { value: 'Place', label: 'Place' },
  { value: 'Organization', label: 'Organization' }
];

const fieldGroups: FieldGroup[] = [
  {
    title: 'Basic Information',
    fields: [
      { path: 'name', label: 'Name', type: 'text', required: true, description: 'The name of the item' },
      { path: 'description', label: 'Description', type: 'text', multiline: true, rows: 3, description: 'A detailed description' },
      { path: '@type', label: 'Schema Type', type: 'array', required: true, description: 'The schema.org type(s) - can be single or multiple' },
      { path: 'url', label: 'URL', type: 'text', description: 'The canonical URL of the item' },
      { path: 'alternateName', label: 'Alternate Names', type: 'array', description: 'Alternative names for the item' },
      { path: 'image', label: 'Image URL', type: 'text', description: 'Main image URL' }
    ]
  },
  {
    title: 'Location & Address',
    fields: [
      { path: 'address.streetAddress', label: 'Street Address', type: 'text', description: 'Street number and name' },
      { path: 'address.addressLocality', label: 'City', type: 'text', description: 'City or locality' },
      { path: 'address.addressRegion', label: 'State/Region', type: 'text', description: 'State or region code' },
      { path: 'address.postalCode', label: 'Postal Code', type: 'text', description: 'ZIP or postal code' },
      { path: 'address.addressCountry', label: 'Country', type: 'text', placeholder: 'US', description: 'Country code (e.g., US, UK)' },
      { path: 'geo.latitude', label: 'Latitude', type: 'number', description: 'Geographic latitude' },
      { path: 'geo.longitude', label: 'Longitude', type: 'number', description: 'Geographic longitude' },
      { path: 'hasMap', label: 'Map URL', type: 'text', description: 'Google Maps or other map URL' }
    ]
  },
  {
    title: 'Contact Information',
    fields: [
      { path: 'telephone', label: 'Phone Number', type: 'text', placeholder: '+1-555-123-4567', description: 'Contact phone number with country code' },
      { path: 'email', label: 'Email', type: 'text', placeholder: 'contact@example.com', description: 'Contact email address' },
      { path: 'website', label: 'Website', type: 'text', description: 'Official website URL' }
    ]
  },
  {
    title: 'Business Hours',
    fields: [
      { path: 'openingHours', label: 'Opening Hours', type: 'array', placeholder: 'Mo-Fr 09:00-17:00', description: 'Regular opening hours (e.g., Mo-Fr 09:00-17:00)' },
      { path: 'openingHoursSpecification', label: 'Detailed Hours', type: 'json', description: 'Detailed opening hours specification' },
      { path: 'specialOpeningHoursSpecification', label: 'Special Hours', type: 'json', description: 'Holiday or special event hours' }
    ]
  },
  {
    title: 'Pricing & Offers',
    fields: [
      { path: 'priceRange', label: 'Price Range', type: 'text', placeholder: '$$ or $10-$50', description: 'Price range indicator' },
      { path: 'offers.price', label: 'Price', type: 'text', description: 'Specific price (number only)' },
      { path: 'offers.priceCurrency', label: 'Currency', type: 'text', placeholder: 'USD', description: 'Currency code' },
      { path: 'offers.availability', label: 'Availability', type: 'select', options: [
        { value: 'https://schema.org/InStock', label: 'In Stock' },
        { value: 'https://schema.org/OutOfStock', label: 'Out of Stock' },
        { value: 'https://schema.org/PreOrder', label: 'Pre-Order' },
        { value: 'https://schema.org/SoldOut', label: 'Sold Out' }
      ], description: 'Availability status' },
      { path: 'offers.validFrom', label: 'Valid From', type: 'text', placeholder: '2024-01-01', description: 'Offer start date' },
      { path: 'offers.validThrough', label: 'Valid Through', type: 'text', placeholder: '2024-12-31', description: 'Offer end date' },
      { path: 'acceptedPaymentMethod', label: 'Accepted Payment Methods', type: 'array', description: 'List of accepted payment methods' }
    ]
  },
  {
    title: 'Features & Amenities',
    fields: [
      { path: 'amenityFeature', label: 'Amenity Features', type: 'json', description: 'List of amenities and features' },
      { path: 'accessibilityFeature', label: 'Accessibility Features', type: 'array', description: 'Accessibility accommodations' },
      { path: 'tourDuration', label: 'Tour Duration', type: 'text', placeholder: 'PT2H30M', description: 'Duration in ISO 8601 format' },
      { path: 'visitDuration', label: 'Visit Duration', type: 'text', placeholder: 'PT1H', description: 'Typical visit duration' },
      { path: 'audience', label: 'Target Audience', type: 'json', description: 'Target audience specifications' },
      { path: 'maximumAttendeeCapacity', label: 'Maximum Capacity', type: 'number', description: 'Maximum number of attendees' },
      { path: 'isAccessibleForFree', label: 'Free Access', type: 'boolean', description: 'Is this accessible for free?' },
      { path: 'publicAccess', label: 'Public Access', type: 'boolean', description: 'Is this publicly accessible?' }
    ]
  },
  {
    title: 'Additional Properties',
    fields: [
      { path: 'additionalProperty', label: 'Additional Properties', type: 'json', description: 'Custom properties as JSON' },
      { path: 'potentialAction', label: 'Potential Actions', type: 'json', description: 'Actions users can take' },
      { path: 'bookingUrl', label: 'Booking URL', type: 'text', description: 'URL for reservations/bookings' },
      { path: 'transportInfo', label: 'Transport Information', type: 'json', description: 'Public transport details' },
      { path: 'aggregateRating', label: 'Aggregate Rating', type: 'json', description: 'Overall ratings data' },
      { path: 'review', label: 'Reviews', type: 'json', description: 'Customer reviews' }
    ]
  },
  {
    title: 'Metadata & System Fields',
    fields: [
      { path: '@context', label: 'Context', type: 'text', required: true, placeholder: 'https://schema.org', description: 'Schema context URL' },
      { path: '@id', label: 'ID', type: 'text', description: 'Unique identifier URL' },
      { path: '_schemaMetadata', label: 'Schema Metadata', type: 'json', description: 'Internal metadata (not for deployment)' }
    ]
  }
];

export const AdvancedSchemaEditor: React.FC<AdvancedSchemaEditorProps> = ({
  schema,
  manualEdits = {},
  verificationStatus = {},
  onChange,
  onResetField
}) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['Basic Information']);

  const getFieldValue = (path: string): any => {
    const parts = path.split('.');
    let value = schema;
    for (const part of parts) {
      if (value && typeof value === 'object') {
        value = value[part];
      } else {
        return undefined;
      }
    }
    // Special handling for @type which can be string or array
    if (path === '@type' && typeof value === 'string') {
      return [value];
    }
    return value;
  };

  const isFieldManuallyEdited = (path: string): boolean => {
    return !!manualEdits[path];
  };

  const getVerificationIcon = (path: string) => {
    const status = verificationStatus[path];
    if (!status) return null;

    switch (status.status) {
      case 'verified':
        return (
          <Tooltip title={`Verified (${Math.round(status.confidence * 100)}% confidence)`}>
            <VerifiedIcon color="success" fontSize="small" />
          </Tooltip>
        );
      case 'warning':
        return (
          <Tooltip title={status.warning}>
            <WarningIcon color="warning" fontSize="small" />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const handleArrayFieldChange = (path: string, index: number, value: string) => {
    const currentValue = getFieldValue(path);
    let currentArray: string[] = [];
    
    // Convert to array if needed
    if (Array.isArray(currentValue)) {
      currentArray = currentValue;
    } else if (typeof currentValue === 'string' && currentValue.trim()) {
      currentArray = [currentValue];
    }
    
    const newArray = [...currentArray];
    newArray[index] = value;
    
    // Special handling for @type - if only one value, save as string
    if (path === '@type' && newArray.length === 1) {
      onChange(path, newArray[0]);
    } else {
      onChange(path, newArray);
    }
  };

  const handleArrayAdd = (path: string) => {
    const currentValue = getFieldValue(path);
    let currentArray: string[] = [];
    
    // Convert to array if needed
    if (Array.isArray(currentValue)) {
      currentArray = currentValue;
    } else if (typeof currentValue === 'string' && currentValue.trim()) {
      currentArray = [currentValue];
    }
    
    onChange(path, [...currentArray, '']);
  };

  const handleArrayRemove = (path: string, index: number) => {
    const currentValue = getFieldValue(path);
    let currentArray: string[] = [];
    
    // Convert to array if needed
    if (Array.isArray(currentValue)) {
      currentArray = currentValue;
    } else if (typeof currentValue === 'string' && currentValue.trim()) {
      currentArray = [currentValue];
    }
    
    const newArray = currentArray.filter((_: any, i: number) => i !== index);
    if (path === '@type' && newArray.length === 1) {
      onChange(path, newArray[0]);
    } else {
      onChange(path, newArray.length > 0 ? newArray : undefined);
    }
  };

  const renderField = (field: FieldDefinition) => {
    const value = getFieldValue(field.path);
    const isManuallyEdited = isFieldManuallyEdited(field.path);

    switch (field.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={field.label}
            value={value || ''}
            onChange={(e) => onChange(field.path, e.target.value)}
            multiline={field.multiline}
            rows={field.rows}
            placeholder={field.placeholder}
            helperText={field.description}
            required={field.required}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isManuallyEdited && (
                    <Tooltip title={`Manually edited on ${new Date(manualEdits[field.path].edited_at).toLocaleDateString()}`}>
                      <EditIcon color="primary" fontSize="small" />
                    </Tooltip>
                  )}
                  {getVerificationIcon(field.path)}
                  {isManuallyEdited && onResetField && (
                    <Tooltip title="Reset to AI generated value">
                      <IconButton size="small" onClick={() => onResetField(field.path)}>
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </InputAdornment>
              )
            }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={field.label}
            type="number"
            value={value || ''}
            onChange={(e) => onChange(field.path, e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder={field.placeholder}
            helperText={field.description}
            required={field.required}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isManuallyEdited && <EditIcon color="primary" fontSize="small" />}
                  {getVerificationIcon(field.path)}
                </InputAdornment>
              )
            }}
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={value === true}
                onChange={(e) => onChange(field.path, e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography>{field.label}</Typography>
                {field.description && (
                  <Typography variant="caption" color="text.secondary">
                    {field.description}
                  </Typography>
                )}
              </Box>
            }
          />
        );

      case 'select':
        return (
          <FormControl fullWidth>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => onChange(field.path, e.target.value)}
              label={field.label}
              required={field.required}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {field.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {field.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {field.description}
              </Typography>
            )}
          </FormControl>
        );

      case 'array':
        // Ensure value is an array - convert string to array if needed
        let arrayValue: string[] = [];
        if (Array.isArray(value)) {
          arrayValue = value;
        } else if (typeof value === 'string' && value.trim()) {
          // Convert string to array (useful for fields like openingHours)
          arrayValue = [value];
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
          // If it's an object, don't try to convert it
          arrayValue = [];
        }
        
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">{field.label}</Typography>
              {isManuallyEdited && (
                <Tooltip title="Manually edited">
                  <EditIcon color="primary" fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => handleArrayAdd(field.path)} sx={{ ml: 'auto' }}>
                <AddIcon />
              </IconButton>
            </Box>
            {field.description && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {field.description}
              </Typography>
            )}
            {arrayValue.map((item: string, index: number) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                {field.path === '@type' ? (
                  <FormControl fullWidth size="small">
                    <Select
                      value={item}
                      onChange={(e) => handleArrayFieldChange(field.path, index, e.target.value as string)}
                      displayEmpty
                    >
                      <MenuItem value="">
                        <em>Select type...</em>
                      </MenuItem>
                      {schemaTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    value={item}
                    onChange={(e) => handleArrayFieldChange(field.path, index, e.target.value)}
                    placeholder={field.placeholder}
                  />
                )}
                <IconButton size="small" onClick={() => handleArrayRemove(field.path, index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            {arrayValue.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No items. Click + to add.
              </Typography>
            )}
          </Box>
        );

      case 'json':
        let jsonValue = '';
        let jsonError = null;
        
        try {
          jsonValue = value ? JSON.stringify(value, null, 2) : '';
        } catch (e) {
          jsonValue = String(value);
          jsonError = 'Invalid JSON';
        }

        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">{field.label}</Typography>
              {isManuallyEdited && (
                <Tooltip title="Manually edited">
                  <EditIcon color="primary" fontSize="small" sx={{ ml: 1 }} />
                </Tooltip>
              )}
            </Box>
            {field.description && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {field.description}
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              rows={4}
              value={jsonValue}
              onChange={(e) => {
                try {
                  const parsed = e.target.value ? JSON.parse(e.target.value) : undefined;
                  onChange(field.path, parsed);
                } catch (err) {
                  // Keep the text value for now, will validate on blur
                }
              }}
              error={!!jsonError}
              helperText={jsonError}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem'
                }
              }}
              placeholder={field.placeholder || '{\n  "key": "value"\n}'}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  const handleGroupToggle = (groupTitle: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupTitle)
        ? prev.filter(g => g !== groupTitle)
        : [...prev, groupTitle]
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          💡 This editor shows ALL schema fields. Fields marked with <EditIcon fontSize="small" color="primary" /> have been manually edited.
          Use the JSON editor for complex nested structures.
        </Typography>
      </Alert>

      {fieldGroups.map((group) => (
        <Accordion
          key={group.title}
          expanded={expandedGroups.includes(group.title)}
          onChange={() => handleGroupToggle(group.title)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {group.icon}
              <Typography variant="h6">{group.title}</Typography>
              <Chip
                label={`${group.fields.filter(f => getFieldValue(f.path) !== undefined).length}/${group.fields.length}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {group.fields.map((field) => (
                <Grid item xs={12} md={field.type === 'json' || field.type === 'array' ? 12 : 6} key={field.path}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom>
          Completeness Impact
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Each filled field contributes to the schema completeness score. 
          Required fields have a larger impact on the score.
        </Typography>
      </Paper>
    </Box>
  );
};